import React from "react";
import type { SvgIconComponent } from "@mui/icons-material";

import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import AdminPanelSettingsRounded from "@mui/icons-material/AdminPanelSettingsRounded";
import AltRouteRounded from "@mui/icons-material/AltRouteRounded";
import BadgeRounded from "@mui/icons-material/BadgeRounded";
import BoltRounded from "@mui/icons-material/BoltRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import CurrencyExchangeRounded from "@mui/icons-material/CurrencyExchangeRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import DialpadRounded from "@mui/icons-material/DialpadRounded";
import HandshakeRounded from "@mui/icons-material/HandshakeRounded";
import HubRounded from "@mui/icons-material/HubRounded";
import LanRounded from "@mui/icons-material/LanRounded";
import MailRounded from "@mui/icons-material/MailRounded";
import ManageAccountsRounded from "@mui/icons-material/ManageAccountsRounded";
import ManageSearchRounded from "@mui/icons-material/ManageSearchRounded";
import MarkEmailReadRounded from "@mui/icons-material/MarkEmailReadRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import NotificationsRounded from "@mui/icons-material/NotificationsRounded";
import PaymentsRounded from "@mui/icons-material/PaymentsRounded";
import PendingActionsRounded from "@mui/icons-material/PendingActionsRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import PriceChangeRounded from "@mui/icons-material/PriceChangeRounded";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import ReceiptRounded from "@mui/icons-material/ReceiptRounded";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import RequestQuoteRounded from "@mui/icons-material/RequestQuoteRounded";
import RouteRounded from "@mui/icons-material/RouteRounded";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import SellRounded from "@mui/icons-material/SellRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import ShieldRounded from "@mui/icons-material/ShieldRounded";
import SignalCellularAltRounded from "@mui/icons-material/SignalCellularAltRounded";
import SpeedRounded from "@mui/icons-material/SpeedRounded";
import StickyNote2Rounded from "@mui/icons-material/StickyNote2Rounded";
import SummarizeRounded from "@mui/icons-material/SummarizeRounded";
import TextFieldsRounded from "@mui/icons-material/TextFieldsRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import UploadFileRounded from "@mui/icons-material/UploadFileRounded";
import VpnLockRounded from "@mui/icons-material/VpnLockRounded";

/** Keys match `IRoute.id` in src/routes.ts. */
const GROUP_ICONS: Record<string, SvgIconComponent> = {
  "user-management": ManageAccountsRounded,
  companies: BusinessRounded,
  service_provider_group: HubRounded,
  provider: SellRounded,
  customer: HandshakeRounded,
  intelligent_routing: AltRouteRounded,
  message_activity: MonitorHeartRounded,
  billing_management: ReceiptLongRounded,
  configuration: SettingsRounded,
};

/** Keys match `IPageRoute.id` in src/routes.ts. */
const PAGE_ICONS: Record<string, SvgIconComponent> = {
  // User Management
  users: PersonRounded,
  roles: BadgeRounded,
  "role-permissions": AdminPanelSettingsRounded,
  // Companies
  profile: BusinessRounded,
  account: AccountTreeRounded,
  // Service Provider
  service_provider: HubRounded,
  service_provider_cost: RequestQuoteRounded,
  import_service_provider_cost: UploadFileRounded,
  // Provider
  cost_plan: SellRounded,
  import_cost_plan: UploadFileRounded,
  pending_cost_imports: PendingActionsRounded,
  testing: ScienceRounded,
  // Customer
  routing_plan: RouteRounded,
  routing_plan_details: AccountTreeRounded,
  import_routing_plan: UploadFileRounded,
  // Intelligent Routing
  fall_back_routing: AltRouteRounded,
  sub_routing: RouteRounded,
  tps_routing: SpeedRounded,
  "sender-translation": TranslateRounded,
  "content-translation": TextFieldsRounded,
  // Message Activity
  message_tracker: ManageSearchRounded,
  live_gateway: BoltRounded,
  live_monitor: MonitorHeartRounded,
  message_re_submission: ReplayRounded,
  pcap_trace: LanRounded,
  // Billing Management
  customer_bill: ReceiptRounded,
  provider_bill: ReceiptRounded,
  service_provider_bill: ReceiptRounded,
  payment: PaymentsRounded,
  note: StickyNote2Rounded,
  soa: SummarizeRounded,
  repricing: PriceChangeRounded,
  currency: CurrencyExchangeRounded,
  // Configuration
  operators: SignalCellularAltRounded,
  prefixes: DialpadRounded,
  invoice_template: DescriptionRounded,
  rate_template: DescriptionRounded,
  email_template: MailRounded,
  whitelisted_ips: ShieldRounded,
  notifications: NotificationsRounded,
  inbound_email_config: MarkEmailReadRounded,
  profile_cost_import_config: TuneRounded,
  vpn_tunnels: VpnLockRounded,
};

function getGroupIcon(id: string): SvgIconComponent {
  return GROUP_ICONS[id] ?? AccountTreeRounded;
}

function getPageIcon(id: string, groupId?: string): SvgIconComponent {
  return PAGE_ICONS[id] ?? (groupId ? getGroupIcon(groupId) : DescriptionRounded);
}

interface NavIconProps {
  id: string;
  groupId?: string;
  variant?: "page" | "group";
  size?: number;
  color?: string;
}

export const NavIcon: React.FC<NavIconProps> = ({
  id,
  groupId,
  variant = "page",
  size = 20,
  color,
}) => {
  const Icon = variant === "group" ? getGroupIcon(id) : getPageIcon(id, groupId);
  return <Icon sx={{ fontSize: size, color, display: "block" }} />;
};

/** Default pinned set. Entries the user lacks permission for are dropped. */
export const SHORTCUT_PAGE_IDS = [
  "live_monitor",
  "live_gateway",
  "message_tracker",
  "users",
  "customer_bill",
  "routing_plan",
];

/** Full page names do not fit an icon tile. */
export const SHORTCUT_LABELS: Record<string, string> = {
  live_monitor: "Monitor",
  live_gateway: "Gateway",
  message_tracker: "Tracker",
  users: "Users",
  customer_bill: "Bills",
  routing_plan: "Routing",
};
